import * as React from "react";

import { makeStyles } from "@material-ui/styles";
import Card from "@material-ui/core/Card";
import Divider from "@material-ui/core/Divider";

import "react-perfect-scrollbar/dist/css/styles.css";
import PerfectScrollbar from "react-perfect-scrollbar";

import { translate } from "jazasoft";
import Table from "../../../components/Table";
import CardHeader from "../../../components/CardHeader";

import { useDeepMemo, useTime } from "../../../hooks";
import { getDistinctValues, isEmpty } from "../../../utils/helpers";
import { getSortedSizeKeyList } from "../../../helpers/orderHelper";
import { format } from "../../../utils/dateUtils";

const NON_SB_FIELDS = ["id", "orderQty", "label", "product", "color", "flowRef", "delFlowRef", "customerFlowRef", "inseam", "destination", "delivery", "total"];

const useStyles = makeStyles({
  table: {
    "& th, & td": {
      border: "1px solid rgba(224, 224, 224, 1)"
    }
  },
  rowCell: {
    padding: 0,
    fontSize: 12,
    "&:last-child": {
      padding: 0,
      fontSize: 12
    }
  },
  footer: {
    display: "flex",
    flex: 1,
    padding: "1em",
    justifyContent: "space-between"
  }
});

const SizeBreakup = props => {
  const { view, order, selectedRow, translate } = props;
  const classes = useStyles();
  const { utc_offset } = useTime();

  //** Prepare Size Breakup Columns for View mode **/
  const { columns, headerRows } = useDeepMemo(() => {
    if (isEmpty(order?.flowInfoList)) return [];

    let flowInfoList = [];
    let sizeBreakupList = [];
    let hasInseam = false;
    if (view === "order") {
      let children = order?.flowInfoList?.filter(fi => isEmpty(fi.orderParentId)).filter(fi => fi.parentId === selectedRow?.id) || [];
      children = children.flatMap(child => {
        const childList = order?.flowInfoList?.filter(fi => fi.parentId === child?.id) || [];
        return isEmpty(childList) ? [child] : childList;
      });

      flowInfoList = isEmpty(children) ? [selectedRow] : children;
      hasInseam = flowInfoList.some(c => !isEmpty(c.inseam));
      sizeBreakupList = flowInfoList
        .flatMap(fi => fi.sizeBreakupList?.filter(sb => sb.type === "ORDER") || [])
        .map(sb => (hasInseam ? { ...sb, sizeGroup: "G1", sizeKey: `G1_${sb.size}` } : sb));
    } else {
      flowInfoList = selectedRow?.flowInfoList || [];
      hasInseam = flowInfoList.some(c => !isEmpty(c.inseam));
      sizeBreakupList = flowInfoList
        .flatMap(fi => fi.fpoSizeBreakupList || [])
        .map(sb => (hasInseam ? { ...sb, sizeGroup: "G1", sizeKey: `G1_${sb.size}` } : sb));
    }

    const sbMap = sizeBreakupList.reduce((acc, sb) => ({ ...acc, [sb.sizeKey]: sb }), {});
    const sbList = Object.values(sbMap).sort((a, b) => a.serialNo - b.serialNo);
    let columns = [];
    if (flowInfoList?.length > 1) {
      let cols = [
        { dataKey: "product", title: "Product", align: "center" },
        { dataKey: "color", title: "Color", align: "center" },
        { dataKey: "flowRef", title: "Flow Ref", align: "center" },
        { dataKey: "delFlowRef", title: "Del. Flow Ref", align: "center" },
        { dataKey: "customerFlowRef", title: "Customer Flow Ref", align: "center" },
        { dataKey: "inseam", title: "Inseam", align: "center" },
        { dataKey: "destination", title: "Destination", align: "center" },
        { dataKey: "delivery", title: "Delivery Date", align: "center" }
      ];
      const productCount = getDistinctValues(flowInfoList.map(c => c.product?.id))?.length;
      const colorCount = getDistinctValues(flowInfoList.map(c => c.color))?.length;
      const flowRefCount = getDistinctValues(flowInfoList.map(c => c.flowRef))?.length;
      const delFlowRefCount = getDistinctValues(flowInfoList.map(c => c.delFlowRef))?.length;
      const cFlowRefCount = getDistinctValues(flowInfoList.map(c => c.customerFlowRef))?.length;
      const inseamCount = getDistinctValues(flowInfoList.map(c => c.inseam))?.length;
      const destCount = getDistinctValues(flowInfoList.map(c => c.destination))?.length;
      const deliveryCount = getDistinctValues(flowInfoList.map(c => c.delivery))?.length;

      if (productCount <= 1) {
        cols = cols.filter(c => c.dataKey !== "product");
      }
      if (colorCount <= 1) {
        cols = cols.filter(c => c.dataKey !== "color");
      }
      if (flowRefCount <= 1) {
        cols = cols.filter(c => c.dataKey !== "flowRef");
      }
      if (delFlowRefCount <= 1) {
        cols = cols.filter(c => c.dataKey !== "delFlowRef");
      }
      if (cFlowRefCount <= 1) {
        cols = cols.filter(c => c.dataKey !== "customerFlowRef");
      }
      if (inseamCount <= 1) {
        cols = cols.filter(c => c.dataKey !== "inseam");
      }
      if (destCount <= 1) {
        cols = cols.filter(c => c.dataKey !== "destination");
      }
      if (deliveryCount <= 1) {
        cols = cols.filter(c => c.dataKey !== "delivery");
      }
      columns = [...cols];
    }

    const sizeKeys = getSortedSizeKeyList(sbList);
    let groupRow = {};
    let sizeList = [];
    let sizeGroup;
    for (let i = 0; i < sizeKeys.length; i++) {
      const sizeKey = sizeKeys[i];
      const sb = sbList.find(sb => sb.sizeKey === sizeKey);
      let leftBorder = false;
      if (sizeGroup !== sb.sizeGroup) {
        leftBorder = true;
        sizeGroup = sb.sizeGroup;
        groupRow[sizeKey] = sb.sizeGroup;
      }
      sizeList.push({
        dataKey: sizeKey,
        title: sizeKey.split("_")[1],
        element: ({ record, column }) => <span>{record[column.dataKey] || "-"}</span>,
        leftBorder
      });
    }

    const haveSizeGroup = !hasInseam && getDistinctValues(sizeBreakupList.map(sb => sb.sizeGroup).filter(e => !isEmpty(e)))?.length > 0;

    columns = [...columns, ...sizeList, { dataKey: "total", title: "Total", leftBorder: true }];
    columns = columns.map(c => ({
      ...c,
      align: "center",
      colSpan: ({ column, row, rowIdx }) =>
        !haveSizeGroup || rowIdx === 1 || NON_SB_FIELDS.includes(c.dataKey)
          ? 1
          : isEmpty(row[column.dataKey])
          ? 0
          : getDistinctValues(sbList.filter(sb => sb.sizeGroup === row[column.dataKey]).map(sb => sb.sizeKey))?.length
    }));

    let headerRows = [];
    if (!hasInseam && Object.keys(groupRow)?.length > 0) {
      headerRows = [
        groupRow,
        columns.reduce(
          (acc, c) => ({
            ...acc,
            [c.dataKey]: translate(`resources.orders.fields.flowInfo.${c.dataKey}`, {
              _: c.title
            })
          }),
          {}
        )
      ];
    } else {
      headerRows = [
        columns.reduce(
          (acc, c) => ({
            ...acc,
            [c.dataKey]: translate(`resources.orders.fields.flowInfo.${c.dataKey}`, {
              _: c.title
            })
          }),
          {}
        )
      ];
    }

    return { columns, headerRows };
  }, [selectedRow, order]);

  //** Prepare Size Breakup Rows for View mode **/
  const rows = useDeepMemo(() => {
    if (isEmpty(order?.flowInfoList)) return [];

    let flowInfoList = [];
    if (view === "order") {
      let children =
        order?.flowInfoList
          ?.filter(fi => isEmpty(fi.orderParentId))
          .filter(fi => fi.parentId === selectedRow?.id)
          .sort((a, b) => a.serialNo - b.serialNo) || [];
      children = children.flatMap(child => {
        const childList = order?.flowInfoList?.filter(fi => fi.parentId === child?.id) || [];
        return isEmpty(childList) ? [child] : childList;
      });

      flowInfoList = isEmpty(children) ? [selectedRow] : children;
      flowInfoList = flowInfoList
        .sort((a, b) => a.serialNo - b.serialNo)
        .map(fi => ({ ...fi, sbList: fi.sizeBreakupList?.filter(sb => sb.type === "ORDER") }));
    } else {
      flowInfoList = selectedRow?.flowInfoList || [];
      flowInfoList = flowInfoList
        .map(fi => ({ ...fi, colorId: `${fi.product?.id}-${fi.style}-${fi.color}`, sbList: fi.fpoSizeBreakupList }))
        .sort((a, b) => (a.colorId !== b.colorId ? (a.colorId < b.colorId ? -1 : 1) : a.serialNo - b.serialNo));
    }
    const hasInseam = flowInfoList.some(c => !isEmpty(c.inseam));

    let rows = flowInfoList.map(fi => ({
      id: fi.id,
      product: fi.product?.name,
      color: fi.color,
      flowRef: fi.flowRef,
      delFlowRef: fi.delFlowRef,
      customerFlowRef: fi.customerFlowRef,
      inseam: fi.inseam,
      destination: fi.destination,
      delivery: !isEmpty(utc_offset) && !isEmpty(fi.exFactoryDate) ? `${format(fi.exFactoryDate, utc_offset, "dd MMM, yy")}` : "",
      total: fi.sbList?.reduce((acc, sb) => acc + sb.qty, 0),
      orderQty: fi.orderQty,
      ...fi.sbList?.map(sb => (hasInseam ? { ...sb, sizeKey: `G1_${sb.size}` } : sb)).reduce((acc, sb) => ({ ...acc, [sb.sizeKey]: sb.qty }), {})
    }));
    if (rows.every(row => row.total === 0)) {
      rows = [];
    }
    if (rows?.length > 1 && !isEmpty(flowInfoList)) {
      const totalRow = columns
        .filter(c => c.dataKey === "total" || !NON_SB_FIELDS.includes(c.dataKey))
        .reduce((acc, c) => ({ ...acc, [c.dataKey]: rows.reduce((acc, row) => acc + (row[c.dataKey] || 0), 0) }), {});
      const cols = columns.filter(c => c.dataKey !== "total" && NON_SB_FIELDS.includes(c.dataKey));
      if (!isEmpty(cols)) {
        rows.push({ ...totalRow, [cols[cols.length - 1].dataKey]: "Total", isTotalRow: true });
      }
    }
    return rows;
  }, [selectedRow, order, columns]);

  return (
    <Card style={{ marginTop: "1.5em" }}>
      <CardHeader title="Size Breakup" />

      <Divider />
      <PerfectScrollbar>
        <Table
          classes={{ table: classes.nowrapTable }}
          columns={columns}
          headerRows={headerRows}
          rows={rows}
          i18nKey="resources.orders.fields.flowInfo"
          headerCellStyle={column => ({
            width: NON_SB_FIELDS.includes(column?.dataKey) ? 150 : undefined,
            borderLeft: column.leftBorder ? "1px solid rgba(224, 224, 224, 1)" : undefined,
            fontWeight: "bold",
            backgroundColor: "#C6D0DA"
          })}
          cellStyle={({ column, record }) => ({
            fontWeight: record.isTotalRow ? "bold" : "normal",
            borderLeft: column.leftBorder ? "1px solid rgba(224, 224, 224, 1)" : undefined
          })}
        />
      </PerfectScrollbar>
    </Card>
  );
};

export default translate(SizeBreakup);
